// Navigation
import { createFocusTrap } from "focus-trap";
import { onClickOutsideOf } from "../utils";
import { revealElements } from "./reveal";

const SELECTORS = {
  nav: ".js-nav",
  toggleButton: ".js-nav-toggle",
  content: ".nav__content",
  menuItems: ".nav__menu > .nav__item",
  submenuToggleButton: ".js-nav-submenu-toggle",
};

const CLASSES = {
  bodyClass: "nav-open",
  noScroll: "no-scroll",
  isOpen: "is-open",
};

class Navigation {
  constructor(el) {
    this.nav = el;
    this.toggleBtn = this.nav.querySelector(SELECTORS.toggleButton);
    this.content = this.nav.querySelector(SELECTORS.content);
    this.submenuToggleBtns = this.nav.querySelectorAll(SELECTORS.submenuToggleButton);

    this.isOpen = false;
  }

  init() {
    this.focusTrap = createFocusTrap(this.nav, {
      allowOutsideClick: () => true,
      returnFocusOnDeactivate: false,
      onDeactivate: () => this.toggleMenu(false),
    });
    this.bindEvents();
  }

  bindEvents() {
    this.outsideClickHandler = null;
    this.menuOutsideClickHandler = null;
    this.toggleBtn.addEventListener("click", this.toggleMenu);

    if (this.submenuToggleBtns.length) {
      Array.from(this.submenuToggleBtns).forEach((btn) => {
        btn.addEventListener("click", (e) => {
          e.preventDefault();
          this.toggleSubmenu(btn);
        });
      });
    }
  }

  toggleMenu = (force) => {
    const isOpen = typeof force === "boolean" ? force : !this.isOpen;

    if (isOpen === this.isOpen) {
      return;
    }

    this.isOpen = isOpen;

    this.nav.classList.toggle(CLASSES.isOpen, this.isOpen);
    this.toggleBtn.setAttribute("aria-expanded", String(this.isOpen));
    document.body.classList.toggle(CLASSES.bodyClass, this.isOpen);

    if (this.isOpen) {
      this.lockScroll();
      revealElements(this.content.querySelectorAll(SELECTORS.menuItems), {
        duration: 0.4,
        stagger: 0.05,
      });
      window.setTimeout(() => {
        this.menuOutsideClickHandler = onClickOutsideOf(this.content, () => {
          if (this.isOpen) {
            this.toggleMenu(false);
          }
        });
      }, 200);
      this.focusTrap.activate();
    } else {
      if (this.menuOutsideClickHandler) {
        this.menuOutsideClickHandler();
        this.menuOutsideClickHandler = null;
      }
      this.unlockScroll();
      this.focusTrap.deactivate();
      this.toggleBtn.focus({ preventScroll: true });
    }
  };

  lockScroll() {
    document.body.classList.add(CLASSES.noScroll);
  }

  unlockScroll() {
    document.body.classList.remove(CLASSES.noScroll);
  }

  toggleSubmenu = (btn) => {
    const submenuId = btn.getAttribute("aria-controls");
    const isOpen = btn.getAttribute("aria-expanded") === "true";

    const submenu = document.getElementById(submenuId);
    const menuItem = btn.parentNode;

    if (!submenu) {
      return false;
    }

    if (!isOpen) {
      window.setTimeout(() => {
        this.outsideClickHandler = onClickOutsideOf(submenu, () => this.closeAllSubmenus());
      }, 200);
    }

    this.closeAllSubmenus();
    menuItem.classList.toggle(CLASSES.isOpen, !isOpen);
    btn.setAttribute("aria-expanded", String(!isOpen));
  };

  closeAllSubmenus() {
    if (this.outsideClickHandler) {
      this.outsideClickHandler();
      this.outsideClickHandler = null;
    }
    Array.from(this.submenuToggleBtns).forEach((btn) => {
      btn.parentNode.classList.remove(CLASSES.isOpen);
      btn.setAttribute("aria-expanded", "false");
    });
  }
}

const navElement = document.querySelector(SELECTORS.nav);
if (navElement) {
  const nav = new Navigation(navElement);
  nav.init();
}
